import { ALBUMS, SHAREABLE_CHANNELS } from '@disco/constants/FeatureTypes'
import type { GROUP_TYPE } from '@disco/constants/GroupTypes'
import { CUSTOM_CHANNEL, MAX_NESTING } from '@disco/constants/GroupTypes'
import type { PaneType } from '@disco/constants/PaneTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import type { ViewType } from '@disco/constants/ViewTypes'
import { ALBUM_VIEW, DEFAULT_VIEW } from '@disco/constants/ViewTypes'
import type { SimSearchQuery } from '@disco/state-management/reducers/search'
import {
  makeGetFeatureData,
  makeIsFeatureEnabled,
} from '@disco/state-management/selectors/business'
import {
  getIsSubfolder,
  makeGetGroupLocks,
} from '@disco/state-management/selectors/groups'
import { getUser } from '@disco/state-management/selectors/user'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction } from 'react-router-dom'
import type { Group, GroupId } from 'types/group'
import type { PopoverItemType } from 'types/ui'
import {
  deleteGroup,
  updateGroup,
} from '@disco/state-management/actions/GroupActions'
import { updateSearchParams } from '@disco/state-management/actions/SearchActions'
import EditCustomLibraryForm from 'components/common/custom-libraries/edit-form'
import GroupModal from 'components/common/custom-libraries/edit-form'
import RenameDialog from 'components/common/rename-dialog'
import {
  channelViewOptions,
  playlistOptions,
  tagFilterOption,
  trackOptions,
} from 'components/explorer/pane/search-header/select-options'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import DeleteGroupDialog from '../../modals/dialogs/delete-group-dialog'
import type { RootState } from 'store/configureStore'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'

const mapStateToProps = (state: RootState, { group }: { group: Group }) => {
  const getGroupLocks = makeGetGroupLocks()
  const isFeatureEnabled = makeIsFeatureEnabled()
  const isFeatureEnabled3 = makeIsFeatureEnabled()
  const shareableChannelsEnabled = isFeatureEnabled3(state, {
    featureName: SHAREABLE_CHANNELS,
  })
  const getFeatureData = makeGetFeatureData()
  const { isTrial, badgeTitle } = getFeatureData(state, {
    featureName: SHAREABLE_CHANNELS,
  })
  return {
    user: getUser(state),
    searchType: state.search.type,
    disableDestructiveActions: group
      ? getGroupLocks(state, {
          groupId: group.id,
        })?.lock_structure
      : false,
    albumsEnabled: isFeatureEnabled(state, {
      featureName: ALBUMS,
    }),
    isSubfolder: group
      ? getIsSubfolder(state, {
          groupId: group.id,
        })
      : false,
    shareableChannelsEnabled,
    isTrial,
    badgeTitle,
  }
}

const connector = connect(mapStateToProps, {
  updateSearchParams,
  updateGroup,
  deleteGroup,
  sendMixpanelEvent,
})

type ReduxProps = ConnectedProps<typeof connector>

type Props = ReduxProps & {
  navigate?: NavigateFunction
  pane?: {
    groupType: GROUP_TYPE
    groupId: GroupId
    artistSortParam: SORT | null | undefined
    artistSortDirection: SORT_DIRECTION | null | undefined
    trackSortParam: SORT | null | undefined
    trackSortDirection: SORT_DIRECTION | null | undefined
    playlistSortParam: SORT
    playlistSortDirection: SORT_DIRECTION
    view: ViewType | null | undefined
    type: PaneType
    index: number
    simSearch: SimSearchQuery | null | undefined
  }
  group: Group | null | undefined
  groupDepth: number
  onViewSwitch?: (view: ViewType | null | undefined, value?: string) => unknown
  sortingOptions?: PopoverItemType[]
  right?: boolean
  compact?: boolean
}

class GroupItemActions extends React.Component<
  Props,
  {
    showNewGroupModal: boolean
    showDeleteDialog: boolean
    showEditGroupModal: boolean
    showRenameDialog: boolean
  }
> {
  state = {
    showNewGroupModal: false,
    showDeleteDialog: false,
    showEditGroupModal: false,
    showRenameDialog: false,
  }
  _sortOptions: HTMLElement | null | undefined
  _onOptionChoose = (value: string) => {
    const { onViewSwitch, pane } = this.props

    if (value !== tagFilterOption.value) {
      if (pane && onViewSwitch) onViewSwitch(pane.view, value)
    }
  }
  _openNewGroupModal = () => {
    this.setState({
      showNewGroupModal: true,
    })
  }
  _closeNewGroupModal = () => {
    this.setState({
      showNewGroupModal: false,
    })
  }
  _toggleDeleteConfirmationDialog = () => {
    this.setState({
      showDeleteDialog: !this.state.showDeleteDialog,
    })
  }
  _toggleEditGroupModal = () => {
    this.setState({
      showEditGroupModal: !this.state.showEditGroupModal,
    })
  }
  _toggleRenameDialog = () => {
    this.setState({
      showRenameDialog: !this.state.showRenameDialog,
    })
  }
  _handleRename = (name: string) => {
    if (!this.props.group) return
    this.props.updateGroup({
      id: this.props.group.id,
      name,
    })

    this._toggleRenameDialog()
  }
  _handleRemoveClick = () => {
    if (this.props.group) {
      this.props.deleteGroup(this.props.group)
    }

    this._toggleDeleteConfirmationDialog()
  }
  _handleShareClick = () => {
    if (!this.props.group || !this.props.navigate) return
    this.props.navigate(`/explorer/channels/${this.props.group.id}/share`)
  }

  _handleMenuOpen = () => {
    sendMixpanelEvent('Open Menu', {
      Context: 'Channel',
      Menu: 'Channel',
      Location: 'Explorer',
    })
  }

  render() {
    const {
      group,
      groupDepth,
      pane,
      onViewSwitch,
      disableDestructiveActions,
      sortingOptions,
    } = this.props
    if (!group) return null
    const isChannel = !group.parent
    const { permissions, business_role } = group
    const viewOptions = this.props.albumsEnabled
      ? channelViewOptions
      : channelViewOptions.filter(option => option.value !== ALBUM_VIEW)
    const isFolder = !isChannel && !this.props.isSubfolder
    const folderType = isChannel ? 'Add folder' : isFolder && 'Add subfolder'
    const isRenameVisible = business_role === 'owner'
    const isRenameDisabled = !permissions.rename
    const isCreateVisible = business_role === 'owner'
    const isCreateFolderDisabled =
      !permissions.structure || groupDepth >= MAX_NESTING
    const isDeleteVisible = business_role === 'owner'
    const isDeleteDisabled = !permissions.delete
    const isLeaveChannelVisible = isChannel && business_role === 'partner'
    const isLeaveChannelDisabled = !permissions.manage
    const isShareVisible = isChannel || (!isChannel && permissions.share_link)
    const isShareDisabled = !permissions.share_link && !permissions.manage
    const options = []

    if (isShareVisible) {
      options.push({
        value: 'share',
        label: 'Share',
        disabled: isShareDisabled,
        tooltip: isShareDisabled
          ? 'Only channel managers can do this'
          : undefined,
      })
    }

    if (pane) {
      options.push({
        value: 'show',
        label: 'Show',
        nested: viewOptions.map(option => {
          if ((pane.view || DEFAULT_VIEW) === option.value)
            return {
              selected: true,
              ...option,
              icon: undefined,
            }
          return { ...option, icon: undefined }
        }),
      })
      options.push({
        value: 'sort',
        label: 'Sorting',
        nested: sortingOptions,
      })
    }

    if (folderType && isCreateVisible) {
      options.push({
        value: 'add_folder',
        disabled: isCreateFolderDisabled,
        label: folderType, // Ensure label is included
      })
    }

    if (isRenameVisible) {
      options.push({
        value: group.parent ? 'edit_folder' : 'edit_channel',
        label: 'Rename',
        disabled: isRenameDisabled,
        tooltip: isRenameDisabled
          ? 'Only channel managers can do this'
          : undefined,
      })
    }

    if (isDeleteVisible) {
      options.push({
        value: 'delete',
        label: 'Delete',
        destructive: true,
        disabled: isDeleteDisabled,
        tooltip: isDeleteDisabled
          ? 'Only channel managers can do this'
          : undefined,
      })
    }

    if (isLeaveChannelVisible) {
      options.push({
        value: 'delete',
        label: 'Leave channel',
        destructive: true,
        disabled: isLeaveChannelDisabled,
        tooltip: isLeaveChannelDisabled
          ? 'Only channel managers can do this'
          : undefined,
      })
    }

    const menu =
      options.length > 0 ? (
        <div className="ExplorerPane_Header_Ellipsis">
          <EllipsisMenu
            buttonSize={this.props.compact ? 'compact' : 'default'}
            right={this.props.right}
            popoverClassName="ExplorerPane_Context_Menu"
            icon="ellipsis-vertical"
            hasArrow={false}
            options={options}
            onOpen={this._handleMenuOpen}
            onOptionClick={(value: string) => {
              if (
                trackOptions.map(o => o.value).includes(value) ||
                playlistOptions.map(o => o.value).includes(value)
              ) {
                this._onOptionChoose(value)

                return
              }

              if (channelViewOptions.map(o => o.value).includes(value)) {
                let channelViewType = value as any as ViewType
                if (onViewSwitch) onViewSwitch(channelViewType)
                return
              }

              switch (value) {
                case 'share':
                  this._handleShareClick()

                  break

                case 'add_folder':
                  this._openNewGroupModal()

                  break

                case 'edit_channel':
                  this._toggleEditGroupModal()

                  break

                case 'edit_folder':
                  this._toggleRenameDialog()

                  break

                case 'delete':
                  this._toggleDeleteConfirmationDialog()

                  break
              }
            }}
          />
        </div>
      ) : null
    let newGroupModal = null

    if (this.state.showNewGroupModal) {
      newGroupModal = (
        <EditCustomLibraryForm
          user={this.props.user}
          groupType={CUSTOM_CHANNEL}
          parentGroup={group}
          onSubmit={this._closeNewGroupModal}
          onClose={this._closeNewGroupModal}
        />
      )
    }

    let deleteConfirmationDialog = null

    if (this.state.showDeleteDialog) {
      deleteConfirmationDialog = (
        <DeleteGroupDialog
          group={group}
          onConfirm={this._handleRemoveClick}
          onCancel={this._toggleDeleteConfirmationDialog}
        />
      )
    }

    let renameDialog = null

    if (this.state.showRenameDialog) {
      renameDialog = (
        <RenameDialog
          typeName={this.props.isSubfolder ? 'subfolder' : 'folder'}
          initialValue={group.name}
          onSubmit={this._handleRename}
          onCancel={this._toggleRenameDialog}
        />
      )
    }

    let editGroupModal = null

    if (this.state.showEditGroupModal) {
      editGroupModal = (
        <GroupModal
          user={this.props.user}
          {...this.props.group}
          groupType={CUSTOM_CHANNEL}
          onSubmit={this._toggleEditGroupModal}
          onClose={this._toggleEditGroupModal}
        />
      )
    }

    return (
      <>
        {menu}
        {newGroupModal}
        {deleteConfirmationDialog}
        {editGroupModal}
        {renameDialog}
      </>
    )
  }
}

const ConnectedGroupItemActions = connector(GroupItemActions)

function GroupItemActionsWithRouter(props: any) {
  const navigate = useNavigate()
  return <ConnectedGroupItemActions {...props} navigate={navigate} />
}

export default GroupItemActionsWithRouter
